import { Fragment, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { useFormikContext } from 'formik'

import { EMPTY_FUNCTION } from '../../../../constants/common'
import {
  useDraggingOver,
  useDragOverZone,
  useInitData,
  useUserData,
} from '../../../../hooks'
import { isEventKeyEnter, preventDefault } from '../../../../utils/events'
import { getHasFilesToShow } from '../../../../utils/file'
import { hasTinyMceHtmlVisibleContent } from '../../../../utils/html'
import { isStaffUser } from '../../../../utils/user'
import {
  AITextGenerationField,
  applyAITextToWysiwygField,
  useAITextGenerationDraftGuard,
} from '../../../AITextGenerationField'
import { AttachActionSelect } from '../../../AttachActionSelect'
import { Avatar, AVATAR_HEIGHT_32 } from '../../../Avatar'
import Button, { BUTTON_HEIGHT_32 } from '../../../Button'
import {
  FileAttachTag,
  FileList,
  useAttachStorageFiles,
  useFileUpload,
} from '../../../File'
import {
  FormActionsAttachContainer,
  FormFieldSelectNG,
  FormInlineNotifications,
  FormUploadAction,
} from '../../../Form'
import { FormikField, FormikWysiwygField } from '../../../Formik'
import Text from '../../../Text'
import {
  CHATROOM_INPUT_FIELD_NAME_MESSAGE,
  CHATROOM_INPUT_FIELD_NAME_TEMPLATE,
  CHATROOM_INPUT_NOTIFICATION_AI_DRAFT_NOT_APPLIED,
  CHATROOM_INPUT_WYSIWYG_MIN_HEIGHT,
  QA_NAME_CHATROOM_INPUT_SEND,
} from '../../Chatroom.constants'
import { useChatroomInputDraft } from '../../Chatroom.hooks'
import { ChatroomInputTemplateSelect } from '../../ChatroomInputTemplateSelect'
import {
  useChatroomInputAttach,
  useChatroomInputTemplate,
  useChatroomInputWysiwygMaxHeight,
} from '../ChatroomInputForm.hooks'
import {
  getChatroomInputIsAIEnabled,
  getChatroomInputIsSendDisabled,
  getChatroomInputTemplatesApiParams,
} from '../ChatroomInputForm.utils'

import * as styles from './ChatroomInputFormFields.module.css'

function ChatroomInputFormFields({
  buildingId,
  canSendAiGeneratedMessage,
  canSendTemplatedMessage,
  draftKey,
  files,
  isFullScreen,
  isPrivate,
  isSubmitting,
  messageDraftRef,
  notifications,
  outboundUuid,
  requestId,
  storageFiles,
  onCloseModal,
  onRemoveNotification,
  onSetFiles,
  onSetModal,
  onSetNotification,
  onSetStorageFiles,
}) {
  const { t } = useTranslation()

  const { submitForm, values } = useFormikContext()

  const currentUser = useUserData()

  const { ai_features: aiFeatures } = useInitData()

  const wysiwygMaxHeight = useChatroomInputWysiwygMaxHeight(isFullScreen)

  useChatroomInputDraft({
    draftKey,
    files,
    messageDraftRef,
    storageFiles,
    onSetFiles,
    onSetStorageFiles,
  })

  const editorRef = useRef(null)

  const {
    filesError,
    filesUploading,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({ files, outboundUuid, onSetFiles })

  const { handleAddStorageFile, handleRemoveStorageFile } =
    useAttachStorageFiles({ onSetFiles: onSetStorageFiles })

  const { handleChangeTemplate, handleClearTemplate } =
    useChatroomInputTemplate({
      addFiles: handleUploadFile,
      editorRef,
      requestId,
      setModal: onSetModal,
      onCloseModal,
    })

  const { ensureDraftApplied, handleAIModeChange, isAIError } =
    useAITextGenerationDraftGuard({
      notificationId: CHATROOM_INPUT_NOTIFICATION_AI_DRAFT_NOT_APPLIED,
      notifications,
      onRemoveNotification,
      onSetNotification,
    })

  const { attachActionOptions, handleAttachAction } = useChatroomInputAttach({
    outboundUuid,
    selectedStorageFiles: storageFiles,
    onAttachFilesFromStorage: handleAddStorageFile,
    onCloseModal,
    onSetModal,
  })

  const {
    handleDragOver,
    handleDropFile,
    isDragging,
    ref: draggingRef,
  } = useDraggingOver()

  const {
    attachOver,
    handleDragAttachLeave,
    handleDragAttachOver,
    handleDragInlineLeave,
    handleDragInlineOver,
    inlineOver,
  } = useDragOverZone({ onDragOver: handleDragOver })

  function handleApplyAIText(generatedText, currentValue, action) {
    return applyAITextToWysiwygField({
      action,
      currentValue,
      editorRef,
      generatedText,
    })
  }

  function handleClickSubmit(event) {
    if (ensureDraftApplied(event)) {
      submitForm()
    }
  }

  function handleInitEditor(editor) {
    editorRef.current = editor
  }

  function handleKeyDownTemplate(event) {
    if (isEventKeyEnter(event)) {
      preventDefault(event)
    }
  }

  function handleRemoveFileTag(file) {
    // Storage-attached files are existing documents, not freshly uploaded
    // ones — detach them from the message without deleting the document.
    if (storageFiles.find(f => f.id === file.id)) {
      handleRemoveStorageFile(file.id)
    } else {
      handleRemoveFile(file.id)
    }
  }

  function renderFile(file, key) {
    return (
      <FileAttachTag
        file={file}
        isDisabled={isSubmitting}
        key={key}
        onClick={EMPTY_FUNCTION}
        onRemove={handleRemoveFileTag}
      />
    )
  }

  const canAttachFromStorage = isStaffUser(currentUser)

  const allFiles = [...storageFiles, ...files]

  const hasMessageContent = hasTinyMceHtmlVisibleContent(
    values[CHATROOM_INPUT_FIELD_NAME_MESSAGE]
  )

  const isAIEnabled = getChatroomInputIsAIEnabled({
    aiFeatures,
    canSendAiGeneratedMessage,
  })

  const isSendDisabled = getChatroomInputIsSendDisabled({
    files: allFiles,
    filesUploading,
    hasMessageContent,
    isSubmitting,
  })

  const showFiles = getHasFilesToShow({
    files: allFiles,
    filesError,
    filesUploading,
  })

  const templatesApiParams = getChatroomInputTemplatesApiParams(buildingId)

  const formClassName = classNames(styles.form, {
    [styles.formPrivate]: isPrivate,
  })

  const editorZoneDraggingClassName = classNames(styles.editorZoneDragging, {
    [styles.editorZoneOver]: inlineOver,
  })

  const editorZoneTextClassName = classNames({
    [styles.editorZoneText]: inlineOver,
  })

  const wysiwyg = (
    <div
      {...(!isSubmitting
        ? { onDragLeaveCapture: handleDragInlineLeave }
        : null)}
      className={styles.editorZone}
    >
      <FormikWysiwygField
        {...(!isSubmitting
          ? { onDragOver: handleDragInlineOver, onDrop: handleDropFile }
          : null)}
        enableInlineImageAttach
        enablePasteAutolink
        enableTables
        Field={Fragment}
        maxHeight={wysiwygMaxHeight}
        minHeight={CHATROOM_INPUT_WYSIWYG_MIN_HEIGHT}
        name={CHATROOM_INPUT_FIELD_NAME_MESSAGE}
        onInit={handleInitEditor}
      />
      {!isSubmitting && isDragging ? (
        <div className={editorZoneDraggingClassName}>
          <Text isSecondary className={editorZoneTextClassName}>
            {t('Chatroom.DropAsImageInTextKey')}
          </Text>
        </div>
      ) : null}
    </div>
  )

  return (
    <div className={formClassName} ref={draggingRef}>
      <Avatar
        color={currentUser?.avatar_color}
        size={AVATAR_HEIGHT_32}
        src={currentUser?.avatar}
        text={currentUser?.fullname}
      />
      <div className={styles.body}>
        {canSendTemplatedMessage ? (
          <FormikField
            Component={FormFieldSelectNG}
            Select={ChatroomInputTemplateSelect}
            apiParams={templatesApiParams}
            controlProps={{
              contentProps: {
                inputProps: {
                  placeholder: t('Chatroom.InputTemplatePlaceholderKey'),
                  onKeyDown: handleKeyDownTemplate,
                },
              },
            }}
            name={CHATROOM_INPUT_FIELD_NAME_TEMPLATE}
            onChange={handleChangeTemplate}
            onClear={handleClearTemplate}
          />
        ) : null}
        {isAIEnabled ? (
          <AITextGenerationField
            disabled={isSubmitting}
            fieldContent={values[CHATROOM_INPUT_FIELD_NAME_MESSAGE]}
            fieldName={CHATROOM_INPUT_FIELD_NAME_MESSAGE}
            hasExistingContent={hasMessageContent}
            isError={isAIError}
            onApplyText={handleApplyAIText}
            onModeChange={handleAIModeChange}
          >
            {wysiwyg}
          </AITextGenerationField>
        ) : (
          wysiwyg
        )}
        <FormInlineNotifications
          className={styles.notifications}
          notifications={notifications}
        />
        {showFiles ? (
          <FileList
            className={styles.fileList}
            errorItems={filesError}
            files={allFiles}
            itemClassName={styles.fileListItem}
            renderItem={renderFile}
            uploadingItems={filesUploading}
            onRemoveFile={handleRemoveFile}
          />
        ) : null}
        <FormActionsAttachContainer
          {...(!isSubmitting
            ? {
                isDragging,
                isOver: attachOver,
                onDragLeave: handleDragAttachLeave,
                onDragOver: handleDragAttachOver,
              }
            : null)}
          className={styles.actions}
          uploadProps={{
            disabled: isSubmitting,
            outboundUuid,
            onDrop: handleDropFile,
            onFinishUpload: handleUploadFile,
            onStartUpload: handleStartUploading,
            onUploadErrors: handleUploadErrors,
          }}
        >
          <Button.Contained
            disabled={isSendDisabled}
            height={BUTTON_HEIGHT_32}
            isLoading={isSubmitting}
            qaName={QA_NAME_CHATROOM_INPUT_SEND}
            text={t('Chatroom.InputSendButtonKey')}
            onClick={handleClickSubmit}
          />
          {canAttachFromStorage ? (
            <AttachActionSelect
              disabled={isSubmitting}
              options={attachActionOptions}
              outboundUuid={outboundUuid}
              placeholder='Chatroom.InputAttachButtonKey'
              onClick={handleAttachAction}
              onFinishUpload={handleUploadFile}
              onStartUpload={handleStartUploading}
              onUploadErrors={handleUploadErrors}
            />
          ) : (
            <FormUploadAction
              disabled={isSubmitting}
              options={{ noDrag: true }}
              outboundUuid={outboundUuid}
              title='Chatroom.InputAttachButtonKey'
              onFinishUpload={handleUploadFile}
              onStartUpload={handleStartUploading}
              onUploadErrors={handleUploadErrors}
            />
          )}
        </FormActionsAttachContainer>
      </div>
    </div>
  )
}

export default ChatroomInputFormFields
